import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { CONTACT } from "../data";

const links = [
  { label: "Who We Serve", href: "#chapters" },
  { label: "The Offer", href: "#promo" },
  { label: "Founder", href: "#founder" },
  { label: "Contact", href: "#contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="main-nav"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,padding] duration-500 ${
        scrolled ? "bg-navy-deep/85 backdrop-blur-xl border-b border-gold/20 py-3" : "py-5"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("#hero")}
          className="flex items-center gap-3 group"
        >
          <img src="/crane_light.png" alt="Kingdom Advancement" className="h-11 w-11 object-contain transition-transform duration-500 group-hover:-translate-y-0.5" />
          <span className="hidden sm:flex flex-col leading-none text-left">
            <span className="font-display text-xl text-cream tracking-wide">KINGDOM</span>
            <span className="text-[9px] text-gold tracking-luxe mt-0.5">ADVANCEMENT</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              onClick={() => go(l.href)}
              className="relative text-cream/80 text-xs uppercase tracking-[0.18em] font-medium hover:text-gold transition-colors duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-gold after:transition-all after:duration-300 hover:after:w-full"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <a
          href={CONTACT.phoneHref}
          data-testid="nav-phone"
          className="hidden md:inline-flex items-center gap-2 border border-gold/60 text-gold text-xs uppercase tracking-[0.18em] px-5 py-2.5 rounded-full hover:bg-gold hover:text-navy-deep transition-colors duration-300"
        >
          <Phone size={14} /> {CONTACT.phone}
        </a>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-cream"
          aria-label="Menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="md:hidden overflow-hidden bg-navy-deep/95 backdrop-blur-xl border-t border-gold/20"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-${l.href.slice(1)}`}
                  onClick={() => go(l.href)}
                  className="text-left text-cream uppercase tracking-[0.18em] text-sm"
                >
                  {l.label}
                </button>
              ))}
              <a href={CONTACT.phoneHref} className="text-gold uppercase tracking-[0.18em] text-sm flex items-center gap-2">
                <Phone size={15} /> {CONTACT.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
